import axios from 'axios';

// Create a base axios instance
const api = axios.create();

// Track if we're already redirecting to login to prevent multiple redirects
let redirectingToLogin = false;
let currentToken = null;
let updateTokenCallback = null;
let logoutCallback = null;
let isLoggingOut = false;

// Keep track of interceptors to avoid duplicates
let requestInterceptorId = null;
let responseInterceptorId = null;

/**
 * Configure the API instance with token and redirect handling
 * @param {string} token - Authentication token
 * @param {Function} updateToken - Function to update token in context
 * @param {Function} logout - Function to logout user
 */
/**
 * Set the logout state to prevent further API operations
 * @param {boolean} loggingOut - Whether logout is in progress
 */
export const setApiLoggingOut = (loggingOut) => {
  isLoggingOut = loggingOut;
  if (loggingOut) {
    redirectingToLogin = true;
  }
};

export const configureApi = (token, updateToken, logout) => {
  
  // Update our references
  currentToken = token;
  updateTokenCallback = updateToken;
  logoutCallback = logout;
  
  // Set default authorization header
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  
  // Reset flags when configuring with a valid token
  redirectingToLogin = false;
  isLoggingOut = false;

  // Remove existing interceptors to avoid duplicates
  if (requestInterceptorId !== null) {
    api.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    api.interceptors.response.eject(responseInterceptorId);
  }

  // Request interceptor - use current token reference
  requestInterceptorId = api.interceptors.request.use(
    (config) => {
      // Always use the current token reference
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      } else {
        console.warn('No current token available for request');
      }
      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  responseInterceptorId = api.interceptors.response.use(
    (response) => {
      // Handle token refresh in response headers
      const refreshedToken = response.headers.authorization || response.headers.Authorization;
      if (refreshedToken && updateTokenCallback && !isLoggingOut) {
        const newToken = refreshedToken.replace('Bearer ', '');
        currentToken = newToken;
        updateTokenCallback(newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
      return response;
    },
    (error) => {
      // Handle 401 Unauthorized errors - but not if we're already logging out
      if (error.response && error.response.status === 401 && !redirectingToLogin && !isLoggingOut) {
        redirectingToLogin = true;
        isLoggingOut = true;
        
        // Log out and redirect to login page
        if (logoutCallback) {
          logoutCallback();
        } else {
          console.error('No logout callback available, manual redirect required');
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );
};

export default api;
